import apiProvider from "./provider";

/**
 * @typedef {Object} DepthLevel
 * @property {number} price
 * @property {number} quantity
 * @property {number} orderCount
 */

/**
 * @typedef {Object} MarketDepth
 * @property {DepthLevel[]} buy
 * @property {DepthLevel[]} sell
 */

/**
 * @typedef {Object} OHLC
 * @property {number} open
 * @property {number} high
 * @property {number} low
 * @property {number} close
 */

/**
 * @typedef {Object} Quote
 * @property {string} exchange
 * @property {string} segment
 * @property {string} trading_symbol
 * @property {number|null} average_price
 * @property {number|null} bid_quantity
 * @property {number|null} bid_price
 * @property {number} day_change
 * @property {number} day_change_perc
 * @property {number|null} upper_circuit_limit
 * @property {number|null} lower_circuit_limit
 * @property {OHLC} ohlc
 * @property {MarketDepth|null} depth
 * @property {number|null} high_trade_range
 * @property {number|null} implied_volatility
 * @property {number|null} last_trade_quantity
 * @property {number|null} last_trade_time  // Unix timestamp
 * @property {number|null} low_trade_range
 * @property {number} last_price
 * @property {number|null} market_cap
 * @property {number|null} offer_price
 * @property {number|null} offer_quantity
 * @property {number|null} oi_day_change
 * @property {number|null} oi_day_change_percentage
 * @property {number|null} open_interest
 * @property {number|null} previous_open_interest
 * @property {number|null} total_buy_quantity
 * @property {number|null} total_sell_quantity
 * @property {number|null} volume
 * @property {number} week_52_high
 * @property {number} week_52_low
 * @property {any|null} error
 */

/**
 * @typedef {Object} QuotesResponse
 * @property {boolean} success
 * @property {Quote[]} quotes
 */

/**
 * Fetches quotes for multiple securities from Groww in a single request.
 *
 * @param {Array<{exchange: string, segment: string, trading_symbol: string}>} securities
 * @returns {Promise<import('axios').AxiosResponse<QuotesResponse>>}
 */
export const fetchGrowwQuotesBatch = async (securities) => {
  return await apiProvider.post("/groww/quotes/batch", {
    securities,
  });
};

/**
 * Fetches a quote for a single security from Groww.
 *
 * @param {Object} params
 * @param {"NSE" | "BSE"} params.exchange
 * @param {"CASH" | "FNO"} params.segment
 * @param {string} params.trading_symbol
 * @returns {Promise<import('axios').AxiosResponse<{status: string, payload: Quote}>>}
 */
export const fetchGrowwQuote = async (params) => {
  return await apiProvider.get("/groww/quote", {
    params,
  });
};

/**
 * Historical candle API response.
 *
 * @typedef {Object} HistoricalCandleResponse
 * @property {boolean} success
 * Indicates whether the API request was successful.
 *
 * @property {string} period
 * High-level period grouping (e.g., "hourly", "daily").
 *
 * @property {string} interval
 * Candle interval resolution (e.g., "1minute", "5minute", "1day").
 *
 * @property {string} start_time
 * ISO 8601 UTC start time of the requested range.
 * Example: "2026-02-27T09:00:00.000Z"
 *
 * @property {string} end_time
 * ISO 8601 UTC end time of the requested range.
 * Example: "2026-02-27T10:00:00.000Z"
 *
 * @property {Candle[]} candles
 * Array of OHLCV candle data.
 */

/**
 * Represents a single OHLCV candle.
 *
 * @typedef {[
 *   string,  // timestamp (ISO string, exchange time)
 *   number,  // open price
 *   number,  // high price
 *   number,  // low price
 *   number,  // close price
 *   number,  // volume
 *   (number|null) // open interest (nullable)
 * ]} Candle
 *
 * Example:
 * [
 *   "2026-02-27T14:30:00",
 *   1404.7,
 *   1405.1,
 *   1404.7,
 *   1405.1,
 *   9746,
 *   null
 * ]
 */

/**
 * Fetches chart data from Groww API.
 *
 * @param {Object} params
 * @param {"NSE" | "BSE"} params.exchange - Exchange identifier
 * @param {"CASH" | "FNO"} params.segment - Market segment
 * @param {string} params.groww_symbol - Broker-specific trading symbol (e.g. "NSE-RELIANCE")
 * @param {string} params.period - Time period (e.g. "hourly", "daily")
 * @param {string} params.interval - Candle interval (e.g. "1minute", "5minute", "15minute", "30minute", "1hour", "1day")
 * @param {string} params.now_date - Current date/time in ISO format
 *
 * @returns {Promise<import('axios').AxiosResponse<HistoricalCandleResponse>>}
 */
export const fetchGrowwChart = async (params) => {
  return await apiProvider.get("/groww/chart", {
    params,
  });
};
